import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Mail, CheckCircle, XCircle, Users, MessageSquare, Send, Edit, Save, Archive, Shield, ChevronDown, ChevronUp, GripVertical, Repeat } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SettingsPage = () => {
    const { user, logout, isAdmin } = useAuth();
    const [accounts, setAccounts] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [accountDialogOpen, setAccountDialogOpen] = useState(false);
    const [editAccountDialogOpen, setEditAccountDialogOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [memberDialogOpen, setMemberDialogOpen] = useState(false);
    const [newAccount, setNewAccount] = useState({
        email: '',
        app_password: '',
        smtp_host: 'smtp.gmail.com',
        smtp_port: 587,
        imap_host: 'imap.gmail.com',
        imap_port: 993,
        first_name: '',
        last_name: '',
        middle_name: ''
    });
    const [editAccountForm, setEditAccountForm] = useState({
        first_name: '',
        last_name: '',
        middle_name: '',
        signature_html: ''
    });
    const [newMember, setNewMember] = useState({
        email: '',
        password: '',
        role: 'team_member'
    });
    const [webhookUrl, setWebhookUrl] = useState('');
    const [baseUrl, setBaseUrl] = useState('');
    const [testingWebhook, setTestingWebhook] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [campaignSettings, setCampaignSettings] = useState({
        daily_limit_per_account: 50,
        bounce_threshold: 3,
        min_delay_seconds: 60,
        max_delay_seconds: 300
    });
    const [inboxIgnoreList, setInboxIgnoreList] = useState([]);
    const [newIgnoreEntry, setNewIgnoreEntry] = useState('');
    const [savingIgnoreList, setSavingIgnoreList] = useState(false);
    const [gchatSummaryConfig, setGchatSummaryConfig] = useState({ enabled: false, hour: 9, minute: 0 });
    const [savingGchatSummary, setSavingGchatSummary] = useState(false);
    const [sendingGchatSummaryNow, setSendingGchatSummaryNow] = useState(false);

    // Cadence state
    const [cadences, setCadences] = useState([]);
    const [cadenceDialogOpen, setCadenceDialogOpen] = useState(false);
    const [editingCadence, setEditingCadence] = useState(null);
    const [cadenceForm, setCadenceForm] = useState({ name: '', description: '', steps: [] });
    const [savingCadence, setSavingCadence] = useState(false);

    useEffect(() => {
        fetchAccounts();
        fetchInboxIgnoreList();
        fetchCadences();
        if (isAdmin()) {
            fetchTeamMembers();
            fetchGoogleChatConfig();
            fetchBaseUrl();
            fetchCampaignSettings();
        }
    }, []);

    const fetchInboxIgnoreList = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_URL}/inbox-ignore`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInboxIgnoreList(data.inbox_ignore_list || []);
        } catch (e) {
            console.error('Failed to fetch inbox ignore list:', e);
        }
    };

    const addToIgnoreList = async () => {
        const entry = newIgnoreEntry.trim();
        if (!entry) return;
        const valid = entry.startsWith('@')
            ? !entry.slice(1).includes('@')
            : entry.includes('@') && entry.split('@')[1]?.includes('.');
        if (!valid) {
            toast.error('Use full email (noreply@google.com) or domain (@dmarc.postmarkapp.com)');
            return;
        }
        const lower = entry.toLowerCase();
        if (inboxIgnoreList.some((e) => e.toLowerCase() === lower)) {
            toast.error('This entry already exists in the ignore list');
            return;
        }
        const updated = [...inboxIgnoreList, lower];
        const ok = await saveIgnoreList(updated);
        if (!ok) return;
        setNewIgnoreEntry('');
    };

    const removeFromIgnoreList = async (entry) => {
        const updated = inboxIgnoreList.filter((e) => e !== entry);
        await saveIgnoreList(updated);
    };

    const saveIgnoreList = async (list) => {
        if (!isAdmin()) return false;
        try {
            setSavingIgnoreList(true);
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_URL}/inbox-ignore`,
                { inbox_ignore_list: list },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInboxIgnoreList(list);
            toast.success('Ignore list updated');
            return true;
        } catch (e) {
            toast.error(getApiError(e, 'Failed to save ignore list'));
            return false;
        } finally {
            setSavingIgnoreList(false);
        }
    };

    const fetchCampaignSettings = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/campaigns/settings`);
            setCampaignSettings(data);
        } catch (e) {
            console.error('Failed to fetch campaign settings', e);
        }
    };

    const saveCampaignSettings = async () => {
        if (campaignSettings.min_delay_seconds > campaignSettings.max_delay_seconds) {
            toast.error('Min delay cannot exceed max delay');
            return;
        }
        if (campaignSettings.bounce_threshold < 0 || campaignSettings.bounce_threshold > 100) {
            toast.error('Bounce threshold must be between 0 and 100');
            return;
        }
        try {
            setLoadingConfig(true);
            await axios.put(`${API_URL}/campaigns/settings`, campaignSettings);
            toast.success('Campaign settings saved');
        } catch (e) {
            toast.error(getApiError(e, 'Failed to save campaign settings'));
        } finally {
            setLoadingConfig(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await axios.get(`${API_URL}/accounts`);
            setAccounts(response.data);
        } catch (error) {
            toast.error('Failed to fetch accounts');
        }
    };

    const fetchTeamMembers = async () => {
        try {
            const response = await axios.get(`${API_URL}/team/members`);
            setTeamMembers(response.data);
        } catch (error) {
            toast.error('Failed to fetch team members');
        }
    };

    const createAccount = async () => {
        if (!newAccount.email || !newAccount.app_password) {
            toast.error('Email and app password are required');
            return;
        }

        try {
            await axios.post(`${API_URL}/accounts`, newAccount);
            toast.success('Account connected successfully');
            setAccountDialogOpen(false);
            setNewAccount({
                email: '',
                app_password: '',
                smtp_host: 'smtp.gmail.com',
                smtp_port: 587,
                imap_host: 'imap.gmail.com',
                imap_port: 993,
                first_name: '',
                last_name: '',
                middle_name: ''
            });
            fetchAccounts();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to connect account'));
        }
    };

    const openEditAccountDialog = (account) => {
        setEditingAccount(account);
        setEditAccountForm({
            first_name: account.first_name || '',
            last_name: account.last_name || '',
            middle_name: account.middle_name || '',
            signature_html: account.signature_html || ''
        });
        setEditAccountDialogOpen(true);
    };

    const updateAccount = async () => {
        if (!editingAccount) return;

        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_URL}/accounts/${editingAccount.id}`,
                editAccountForm,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Account updated successfully');
            setEditAccountDialogOpen(false);
            setEditingAccount(null);
            fetchAccounts();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to update account'));
        }
    };

    const deleteAccount = async (accountId) => {
        if (!window.confirm('Are you sure you want to delete this account?')) return;

        try {
            await axios.delete(`${API_URL}/accounts/${accountId}`);
            toast.success('Account deleted successfully');
            fetchAccounts();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to delete account'));
        }
    };

    const createTeamMember = async () => {
        if (!newMember.email || !newMember.password) {
            toast.error('Email and password are required');
            return;
        }

        try {
            await axios.post(`${API_URL}/team/members`, newMember);
            toast.success('Team member added successfully');
            setMemberDialogOpen(false);
            setNewMember({
                email: '',
                password: '',
                role: 'team_member'
            });
            fetchTeamMembers();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to add team member'));
        }
    };

    const deleteTeamMember = async (memberId) => {
        if (!window.confirm('Are you sure you want to remove this team member?')) return;

        try {
            await axios.delete(`${API_URL}/team/members/${memberId}`);
            toast.success('Team member removed successfully');
            fetchTeamMembers();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to remove team member'));
        }
    };

    const fetchGoogleChatConfig = async () => {
        const [webhookResult, summaryResult] = await Promise.allSettled([
            axios.get(`${API_URL}/system-config/google-chat-webhook`),
            axios.get(`${API_URL}/system-config/gchat-daily-summary`),
        ]);
        if (webhookResult.status === 'fulfilled') {
            setWebhookUrl(webhookResult.value.data.webhook_url || '');
        } else {
            console.error('Failed to fetch Google Chat webhook config:', webhookResult.reason);
            toast.error('Failed to fetch Google Chat webhook config');
        }
        if (summaryResult.status === 'fulfilled') {
            setGchatSummaryConfig(summaryResult.value.data || { enabled: false, hour: 9, minute: 0 });
        } else {
            console.error('Failed to fetch daily summary config:', summaryResult.reason);
            toast.error('Failed to fetch daily summary config');
        }
    };

    const fetchBaseUrl = async () => {
        try {
            const response = await axios.get(`${API_URL}/system-config/base-url`);
            setBaseUrl(response.data.base_url || '');
        } catch (error) {
            toast.error(`Failed to fetch BASE_URL config${error.message ? `: ${error.message}` : ''}`);
        }
    };

    const saveWebhookUrl = async () => {
        try {
            setLoadingConfig(true);
            await axios.put(`${API_URL}/system-config/google-chat-webhook`, {
                webhook_url: webhookUrl
            });
            toast.success('Webhook URL saved successfully');
        } catch (error) {
            toast.error(getApiError(error, 'Failed to save webhook URL'));
        } finally {
            setLoadingConfig(false);
        }
    };

    const saveBaseUrl = async () => {
        try {
            setLoadingConfig(true);
            await axios.put(`${API_URL}/system-config/base-url`, {
                base_url: baseUrl
            });
            toast.success('BASE_URL saved successfully');
        } catch (error) {
            toast.error(getApiError(error, 'Failed to save BASE_URL'));
        } finally {
            setLoadingConfig(false);
        }
    };

    const testWebhook = async () => {
        try {
            setTestingWebhook(true);
            const response = await axios.post(`${API_URL}/system-config/google-chat-webhook/test`);
            toast.success('Test message sent successfully! Check your Google Chat.');
        } catch (error) {
            toast.error(getApiError(error, 'Failed to send test message'));
        } finally {
            setTestingWebhook(false);
        }
    };

    const saveGchatSummaryConfig = async () => {
        try {
            setSavingGchatSummary(true);
            await axios.put(`${API_URL}/system-config/gchat-daily-summary`, gchatSummaryConfig);
            toast.success('Daily summary config saved');
        } catch (error) {
            toast.error(getApiError(error, 'Failed to save daily summary config'));
        } finally {
            setSavingGchatSummary(false);
        }
    };

    const sendGchatSummaryNow = async () => {
        try {
            setSendingGchatSummaryNow(true);
            await axios.post(`${API_URL}/system-config/gchat-daily-summary/send-now`);
            toast.success('Daily summary sent! Check your Google Chat space.');
        } catch (error) {
            toast.error(getApiError(error, 'Failed to send daily summary'));
        } finally {
            setSendingGchatSummaryNow(false);
        }
    };

    const fetchCadences = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/cadences`);
            setCadences(data);
        } catch (e) {
            console.error('Failed to fetch cadences', e);
        }
    };

    const openNewCadence = () => {
        setEditingCadence(null);
        setCadenceForm({ name: '', description: '', steps: [] });
        setCadenceDialogOpen(true);
    };

    const openEditCadence = (cadence) => {
        setEditingCadence(cadence);
        setCadenceForm({ name: cadence.name, description: cadence.description || '', steps: cadence.steps.map(s => ({ ...s })) });
        setCadenceDialogOpen(true);
    };

    const addCadenceStep = () => {
        const nextNum = cadenceForm.steps.length + 1;
        setCadenceForm(f => ({
            ...f,
            steps: [...f.steps, { step_number: nextNum, channel: 'call', days_offset: nextNum * 3, description_template: '' }]
        }));
    };

    const updateCadenceStep = (idx, field, value) => {
        setCadenceForm(f => {
            const steps = f.steps.map((s, i) => i === idx ? { ...s, [field]: value } : s);
            return { ...f, steps };
        });
    };

    const removeCadenceStep = (idx) => {
        setCadenceForm(f => {
            const steps = f.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 }));
            return { ...f, steps };
        });
    };

    const saveCadence = async () => {
        if (!cadenceForm.name.trim()) { toast.error('Cadence name is required'); return; }
        if (cadenceForm.steps.length === 0) { toast.error('Add at least one step'); return; }
        try {
            setSavingCadence(true);
            if (editingCadence) {
                await axios.put(`${API_URL}/cadences/${editingCadence.id}`, cadenceForm);
                toast.success('Cadence updated');
            } else {
                await axios.post(`${API_URL}/cadences`, cadenceForm);
                toast.success('Cadence created');
            }
            setCadenceDialogOpen(false);
            fetchCadences();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to save cadence'));
        } finally {
            setSavingCadence(false);
        }
    };

    const deleteCadence = async (id) => {
        if (!window.confirm('Delete this cadence template?')) return;
        try {
            await axios.delete(`${API_URL}/cadences/${id}`);
            toast.success('Cadence deleted');
            fetchCadences();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to delete cadence'));
        }
    };

    return (
        <div className="p-6 md:p-8 lg:p-10 space-y-8" data-testid="settings-page">
            <div>
                <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Settings</h1>
                <p className="text-sm leading-relaxed text-slate-600">
                    {isAdmin() ? 'Manage your email accounts, team members, and preferences' : 'View email accounts and manage your preferences'}
                </p>
            </div>

            {/* User Info */}
            <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                <h2 className="text-xl font-medium text-slate-900 mb-4">Account Information</h2>
                <div className="space-y-3">
                    <div>
                        <Label className="text-slate-600">Email</Label>
                        <p className="text-sm text-slate-900">{user?.email}</p>
                    </div>
                    <div>
                        <Label className="text-slate-600">Role</Label>
                        <p className="text-sm text-slate-900 capitalize">{user?.role || 'team_member'}</p>
                    </div>
                    <div>
                        <Label className="text-slate-600">User ID</Label>
                        <p className="text-sm font-mono text-slate-700">{user?.id}</p>
                    </div>
                    <Button
                        data-testid="logout-button"
                        onClick={logout}
                        variant="destructive"
                        className="mt-4 rounded-lg font-medium transition-all active:scale-95"
                    >
                        Logout
                    </Button>
                </div>
            </Card>

            {/* Team Management (Admin Only) */}
            {isAdmin() && (
                <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-medium text-slate-900">Team Management</h2>
                            <p className="text-sm text-slate-600 mt-1">Add and manage team members</p>
                        </div>
                        <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    data-testid="add-member-button"
                                    className="rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Member
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-2xl rounded-2xl">
                                <DialogHeader>
                                    <DialogTitle className="text-2xl font-semibold text-slate-900">Add Team Member</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                    <div>
                                        <Label>Email Address</Label>
                                        <Input
                                            data-testid="member-email-input"
                                            type="email"
                                            value={newMember.email}
                                            onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                                            placeholder="member@example.com"
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <Label>Password</Label>
                                        <Input
                                            data-testid="member-password-input"
                                            type="password"
                                            value={newMember.password}
                                            onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                                            placeholder="Create a password"
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <Label>Role</Label>
                                        <Select value={newMember.role} onValueChange={(value) => setNewMember({ ...newMember, role: value })}>
                                            <SelectTrigger data-testid="member-role-select" className="rounded-lg">
                                                <SelectValue placeholder="Select role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="team_member">Team Member</SelectItem>
                                                <SelectItem value="admin">Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button
                                        data-testid="create-member-button"
                                        onClick={createTeamMember}
                                        className="w-full rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                    >
                                        Add Team Member
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {teamMembers.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <Users className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                            <p>No team members yet</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {teamMembers.map((member) => (
                                <div
                                    key={member.id}
                                    data-testid={`member-${member.id}`}
                                    className="p-4 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 rounded-lg bg-white border border-slate-200">
                                            <Users className="h-5 w-5 text-slate-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-slate-900">{member.email}</p>
                                            <div className="flex items-center gap-4 mt-1">
                                                <p className="text-xs text-slate-600 capitalize">{member.role}</p>
                                                {member.id === user?.id && (
                                                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-md">You</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {member.id !== user?.id && (
                                        <Button
                                            data-testid={`delete-member-${member.id}`}
                                            onClick={() => deleteTeamMember(member.id)}
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            )}

            {/* Email Accounts */}
            <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-xl font-medium text-slate-900">Connected Email Accounts</h2>
                        <p className="text-sm text-slate-600 mt-1">
                            {isAdmin() ? 'Manage your IMAP/SMTP email connections' : 'View email accounts used for syncing (read-only)'}
                        </p>
                    </div>
                    {isAdmin() && (
                        <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    data-testid="add-account-button"
                                    className="rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Account
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-2xl rounded-2xl">
                                <DialogHeader>
                                    <DialogTitle className="text-2xl font-semibold text-slate-900">Connect Email Account</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                                        <p className="text-sm text-blue-800">
                                            <strong>Note:</strong> For Gmail, you need to generate an App Password. Go to Google Account Settings → Security → 2-Step Verification → App passwords.
                                        </p>
                                    </div>
                                    <div>
                                        <Label>Email Address</Label>
                                        <Input
                                            data-testid="account-email-input"
                                            type="email"
                                            value={newAccount.email}
                                            onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
                                            placeholder="your-email@gmail.com"
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <Label>App Password</Label>
                                        <Input
                                            data-testid="account-password-input"
                                            type="password"
                                            value={newAccount.app_password}
                                            onChange={(e) => setNewAccount({ ...newAccount, app_password: e.target.value })}
                                            placeholder="16-character app password"
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <Label>First Name</Label>
                                            <Input
                                                value={newAccount.first_name}
                                                onChange={(e) => setNewAccount({ ...newAccount, first_name: e.target.value })}
                                                placeholder="John"
                                                className="rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <Label>Middle Name (Optional)</Label>
                                            <Input
                                                value={newAccount.middle_name}
                                                onChange={(e) => setNewAccount({ ...newAccount, middle_name: e.target.value })}
                                                placeholder="Michael"
                                                className="rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <Label>Last Name</Label>
                                            <Input
                                                value={newAccount.last_name}
                                                onChange={(e) => setNewAccount({ ...newAccount, last_name: e.target.value })}
                                                placeholder="Doe"
                                                className="rounded-lg"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>SMTP Host</Label>
                                            <Input
                                                value={newAccount.smtp_host}
                                                onChange={(e) => setNewAccount({ ...newAccount, smtp_host: e.target.value })}
                                                className="rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <Label>SMTP Port</Label>
                                            <Input
                                                type="number"
                                                value={newAccount.smtp_port}
                                                onChange={(e) => setNewAccount({ ...newAccount, smtp_port: parseInt(e.target.value) })}
                                                className="rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <Label>IMAP Host</Label>
                                            <Input
                                                value={newAccount.imap_host}
                                                onChange={(e) => setNewAccount({ ...newAccount, imap_host: e.target.value })}
                                                className="rounded-lg"
                                            />
                                        </div>
                                        <div>
                                            <Label>IMAP Port</Label>
                                            <Input
                                                type="number"
                                                value={newAccount.imap_port}
                                                onChange={(e) => setNewAccount({ ...newAccount, imap_port: parseInt(e.target.value) })}
                                                className="rounded-lg"
                                            />
                                        </div>
                                    </div>
                                    <Button
                                        data-testid="connect-account-button"
                                        onClick={createAccount}
                                        className="w-full rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                    >
                                        Connect Account
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {accounts.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                        <Mail className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                        <p>No email accounts connected yet</p>
                        {isAdmin() && <p className="text-sm">Add an account to start syncing emails</p>}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {accounts.map((account) => (
                            <div
                                key={account.id}
                                data-testid={`account-${account.id}`}
                                className="p-4 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="p-3 rounded-lg bg-white border border-slate-200">
                                        <Mail className="h-5 w-5 text-slate-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">{account.email}</p>
                                        {account.first_name || account.last_name ? (
                                            <p className="text-sm text-slate-600 mt-1">
                                                {[account.first_name, account.middle_name, account.last_name].filter(Boolean).join(' ')}
                                            </p>
                                        ) : null}
                                        <div className="flex items-center gap-4 mt-1">
                                            <p className="text-xs text-slate-600">
                                                {account.smtp_host}:{account.smtp_port}
                                            </p>
                                            <div className="flex items-center gap-1">
                                                {account.is_active ? (
                                                    <>
                                                        <CheckCircle className="h-3 w-3 text-green-600" />
                                                        <span className="text-xs text-green-600">Active</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <XCircle className="h-3 w-3 text-red-600" />
                                                        <span className="text-xs text-red-600">Inactive</span>
                                                    </>
                                                )}
                                            </div>
                                            {account.last_sync && (
                                                <p className="text-xs text-slate-500">
                                                    Last synced: {format(new Date(account.last_sync), 'PPp')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {isAdmin() && (
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => openEditAccountDialog(account)}
                                            variant="ghost"
                                            size="sm"
                                            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                            title="Edit account name"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            data-testid={`delete-account-${account.id}`}
                                            onClick={() => deleteAccount(account.id)}
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Epic 18.4: Inbox Ignore List (Junk Filter) */}
            <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                <div className="flex items-center gap-3 mb-4">
                    <Archive className="h-5 w-5 text-slate-600" />
                    <div>
                        <h2 className="text-xl font-medium text-slate-900">Inbox Ignore List</h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Emails or domains matching this list are marked as junk and hidden from main Inbox/Sent. Add full emails (noreply@google.com) or domains (@dmarc.postmarkapp.com).
                        </p>
                    </div>
                </div>
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <Input
                            value={newIgnoreEntry}
                            onChange={(e) => setNewIgnoreEntry(e.target.value)}
                            placeholder="noreply@google.com or @dmarc.postmarkapp.com"
                            className="rounded-lg flex-1"
                            onKeyDown={(e) => {
                                if (e.key !== 'Enter') return;
                                if (savingIgnoreList || !isAdmin()) {
                                    e.preventDefault();
                                    return;
                                }
                                addToIgnoreList();
                            }}
                        />
                        <Button
                            onClick={addToIgnoreList}
                            disabled={!newIgnoreEntry.trim() || savingIgnoreList || !isAdmin()}
                            className="rounded-lg font-medium bg-slate-900 hover:bg-slate-800 text-white"
                        >
                            Add
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {inboxIgnoreList.length === 0 ? (
                            <p className="text-sm text-slate-500">No entries. Add emails or domains to filter junk.</p>
                        ) : (
                            inboxIgnoreList.map((entry) => (
                                <div
                                    key={entry}
                                    className="flex items-center justify-between p-2 rounded-lg border border-slate-200 bg-slate-50"
                                >
                                    <span className="font-mono text-sm">{entry}</span>
                                    {isAdmin() && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeFromIgnoreList(entry)}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    <p className="text-xs text-slate-500">
                        View filtered emails in Inbox → Junk / Ignored folder.
                    </p>
                </div>
            </Card>

            {/* Integrations - Google Chat (Admin Only) */}
            {isAdmin() && (
                <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                    <div>
                        <h2 className="text-xl font-medium text-slate-900 mb-1">Integrations</h2>
                        <p className="text-sm text-slate-600 mb-6">Configure external integrations</p>

                        {/* Google Chat Section */}
                        <div className="border-t border-slate-200 pt-6 mt-6">
                            <div className="flex items-center gap-3 mb-4">
                                <MessageSquare className="h-5 w-5 text-slate-600" />
                                <h3 className="text-lg font-medium text-slate-900">Google Chat</h3>
                            </div>
                            <p className="text-sm text-slate-600 mb-6">
                                Configure Google Chat webhook to receive real-time notifications when leads reply to your emails.
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="webhook-url">Webhook URL</Label>
                                    <Input
                                        id="webhook-url"
                                        type="url"
                                        value={webhookUrl}
                                        onChange={(e) => setWebhookUrl(e.target.value)}
                                        placeholder="https://chat.googleapis.com/v1/spaces/..."
                                        className="rounded-lg mt-1"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                        Get your webhook URL from Google Chat → Apps → Incoming Webhooks
                                    </p>
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        onClick={saveWebhookUrl}
                                        disabled={loadingConfig}
                                        className="rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                    >
                                        Save Webhook URL
                                    </Button>
                                    <Button
                                        onClick={testWebhook}
                                        disabled={!webhookUrl || testingWebhook}
                                        variant="outline"
                                        className="rounded-lg font-medium transition-all active:scale-95"
                                    >
                                        {testingWebhook ? (
                                            <>
                                                <span className="animate-spin mr-2">⏳</span>
                                                Testing...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="h-4 w-4 mr-2" />
                                                Send Test Message
                                            </>
                                        )}
                                    </Button>
                                </div>

                                <div className="mt-6 pt-6 border-t border-slate-200">
                                    <Label htmlFor="base-url">Base URL</Label>
                                    <Input
                                        id="base-url"
                                        type="url"
                                        value={baseUrl}
                                        onChange={(e) => setBaseUrl(e.target.value)}
                                        placeholder="https://crm.internal.com"
                                        className="rounded-lg mt-1"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                        Base URL for deep links in Google Chat notifications (e.g., https://crm.internal.com)
                                    </p>
                                    <Button
                                        onClick={saveBaseUrl}
                                        disabled={loadingConfig}
                                        variant="outline"
                                        className="mt-2 rounded-lg font-medium transition-all active:scale-95"
                                    >
                                        Save Base URL
                                    </Button>
                                </div>

                                {/* Daily Task Summary Notification */}
                                <div className="mt-6 pt-6 border-t border-slate-200">
                                    <div className="flex items-center gap-3 mb-2">
                                        <MessageSquare className="h-5 w-5 text-slate-600" />
                                        <h3 className="text-lg font-medium text-slate-900">Daily Task Summary</h3>
                                    </div>
                                    <p className="text-sm text-slate-600 mb-4">
                                        Send a morning summary card to your Google Chat space listing every team member's tasks for the day — complete with witty motivation.
                                    </p>
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                id="gchat-summary-enabled"
                                                checked={gchatSummaryConfig.enabled}
                                                onChange={e => setGchatSummaryConfig(c => ({ ...c, enabled: e.target.checked }))}
                                                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                                            />
                                            <Label htmlFor="gchat-summary-enabled" className="cursor-pointer">
                                                Enable daily summary
                                            </Label>
                                        </div>
                                        {gchatSummaryConfig.enabled && (
                                            <div className="flex items-end gap-3 pl-7">
                                                <div className="space-y-1">
                                                    <Label>Hour (0–23)</Label>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={23}
                                                        value={gchatSummaryConfig.hour}
                                                        onChange={e => setGchatSummaryConfig(c => ({ ...c, hour: Math.min(23, Math.max(0, parseInt(e.target.value, 10) || 0)) }))}
                                                        className="rounded-lg w-24"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label>Minute (0–59)</Label>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={59}
                                                        value={gchatSummaryConfig.minute}
                                                        onChange={e => setGchatSummaryConfig(c => ({ ...c, minute: Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)) }))}
                                                        className="rounded-lg w-24"
                                                    />
                                                </div>
                                                <p className="text-xs text-slate-500 pb-1">IST time</p>
                                            </div>
                                        )}
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={saveGchatSummaryConfig}
                                                disabled={savingGchatSummary}
                                                className="rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                            >
                                                {savingGchatSummary ? 'Saving...' : 'Save Summary Config'}
                                            </Button>
                                            <Button
                                                onClick={sendGchatSummaryNow}
                                                disabled={!webhookUrl || sendingGchatSummaryNow}
                                                variant="outline"
                                                className="rounded-lg font-medium transition-all active:scale-95"
                                            >
                                                {sendingGchatSummaryNow ? (
                                                    <><span className="animate-spin mr-2">⏳</span>Sending...</>
                                                ) : (
                                                    <><Send className="h-4 w-4 mr-2" />Send Now</>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 pt-6 border-t border-slate-200">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Send className="h-5 w-5 text-slate-600" />
                                        <h3 className="text-lg font-medium text-slate-900">Campaign Settings</h3>
                                    </div>
                                    <p className="text-sm text-slate-600 mb-4">
                                        Configure bulk email campaign throttling and safety limits.
                                    </p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Daily Limit per Account</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                value={campaignSettings.daily_limit_per_account}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    setCampaignSettings({ ...campaignSettings, daily_limit_per_account: isNaN(val) ? 50 : Math.max(0, val) });
                                                }}
                                                className="rounded-lg mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Label>Bounce Threshold (%)</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                max={100}
                                                value={campaignSettings.bounce_threshold}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    setCampaignSettings({ ...campaignSettings, bounce_threshold: isNaN(val) ? 3 : Math.max(0, Math.min(100, val)) });
                                                }}
                                                className="rounded-lg mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Label>Min Delay (seconds)</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                value={campaignSettings.min_delay_seconds}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    setCampaignSettings({ ...campaignSettings, min_delay_seconds: isNaN(val) ? 60 : Math.max(0, val) });
                                                }}
                                                className="rounded-lg mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Label>Max Delay (seconds)</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                value={campaignSettings.max_delay_seconds}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    setCampaignSettings({ ...campaignSettings, max_delay_seconds: isNaN(val) ? 300 : Math.max(0, val) });
                                                }}
                                                className="rounded-lg mt-1"
                                            />
                                        </div>
                                    </div>
                                    <Button
                                        onClick={saveCampaignSettings}
                                        disabled={loadingConfig}
                                        variant="outline"
                                        className="mt-2 rounded-lg font-medium transition-all active:scale-95"
                                    >
                                        Save Campaign Settings
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Edit Account Dialog */}
            {isAdmin() && (
                <Dialog open={editAccountDialogOpen} onOpenChange={setEditAccountDialogOpen}>
                    <DialogContent className="sm:max-w-2xl rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-semibold text-slate-900">
                                Edit Account Details
                            </DialogTitle>
                        </DialogHeader>
                        {editingAccount && (
                            <div className="space-y-4 mt-4">
                                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                                    <p className="text-sm text-slate-600 mb-1">Email Account</p>
                                    <p className="font-medium text-slate-900">{editingAccount.email}</p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        SMTP: {editingAccount.smtp_host}:{editingAccount.smtp_port}
                                    </p>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <Label>First Name</Label>
                                        <Input
                                            value={editAccountForm.first_name}
                                            onChange={(e) => setEditAccountForm({ ...editAccountForm, first_name: e.target.value })}
                                            placeholder="John"
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <Label>Middle Name (Optional)</Label>
                                        <Input
                                            value={editAccountForm.middle_name}
                                            onChange={(e) => setEditAccountForm({ ...editAccountForm, middle_name: e.target.value })}
                                            placeholder="Michael"
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <Label>Last Name</Label>
                                        <Input
                                            value={editAccountForm.last_name}
                                            onChange={(e) => setEditAccountForm({ ...editAccountForm, last_name: e.target.value })}
                                            placeholder="Doe"
                                            className="rounded-lg"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Email Signature (HTML)</Label>
                                    <Textarea
                                        value={editAccountForm.signature_html}
                                        onChange={(e) => setEditAccountForm({ ...editAccountForm, signature_html: e.target.value })}
                                        placeholder="<p>Best regards,<br/>Your Name</p>"
                                        className="rounded-lg min-h-[100px]"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                        HTML signature that will be appended to emails sent from this account
                                    </p>
                                </div>
                                <div className="flex gap-2 pt-4">
                                    <Button
                                        onClick={updateAccount}
                                        className="flex-1 rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Changes
                                    </Button>
                                    <Button
                                        onClick={() => setEditAccountDialogOpen(false)}
                                        variant="outline"
                                        className="rounded-lg font-medium transition-all active:scale-95"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            )}

            {/* ── Cadence Templates ── */}
            <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-violet-50 rounded-lg"><Repeat className="h-5 w-5 text-violet-600" /></div>
                        <div>
                            <h2 className="text-xl font-medium text-slate-900">Follow-up Cadences</h2>
                            <p className="text-sm text-slate-500">Define multi-step follow-up sequences that auto-create tasks</p>
                        </div>
                    </div>
                    <Button onClick={openNewCadence} className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white">
                        <Plus className="h-4 w-4 mr-2" />New Cadence
                    </Button>
                </div>

                {cadences.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                        <Repeat className="h-10 w-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No cadences yet. Create one to start automating follow-ups.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {cadences.map(cadence => (
                            <div key={cadence.id} className="border border-slate-200 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <p className="font-medium text-slate-900">{cadence.name}</p>
                                        {cadence.description && <p className="text-sm text-slate-500 mt-0.5">{cadence.description}</p>}
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {cadence.steps.map(step => (
                                                <span key={step.step_number} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                                                    <span className="font-semibold text-slate-400">D+{step.days_offset}</span>
                                                    <span className="capitalize">{step.channel}</span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex gap-2 ml-4">
                                        <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openEditCadence(cadence)}>
                                            <Edit className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button size="sm" variant="outline" className="rounded-lg text-red-600 hover:text-red-700 hover:border-red-300" onClick={() => deleteCadence(cadence.id)}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Cadence Create/Edit Dialog */}
            <Dialog open={cadenceDialogOpen} onOpenChange={setCadenceDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingCadence ? 'Edit Cadence' : 'New Cadence'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-5 pt-2">
                        <div className="space-y-1">
                            <Label>Name <span className="text-red-500">*</span></Label>
                            <input
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                                placeholder="e.g. Cold Outreach"
                                value={cadenceForm.name}
                                onChange={e => setCadenceForm(f => ({ ...f, name: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label>Description</Label>
                            <input
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                                placeholder="Optional description"
                                value={cadenceForm.description}
                                onChange={e => setCadenceForm(f => ({ ...f, description: e.target.value }))}
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Steps <span className="text-red-500">*</span></Label>
                                <Button type="button" size="sm" variant="outline" className="rounded-lg text-xs" onClick={addCadenceStep}>
                                    <Plus className="h-3.5 w-3.5 mr-1" />Add Step
                                </Button>
                            </div>

                            {cadenceForm.steps.length === 0 && (
                                <p className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">
                                    No steps yet. Click "Add Step" to begin.
                                </p>
                            )}

                            <div className="space-y-2">
                                {cadenceForm.steps.map((step, idx) => (
                                    <div key={idx} className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg bg-slate-50">
                                        <span className="text-xs font-bold text-slate-400 w-5 text-center">{idx + 1}</span>
                                        <div className="flex-1 grid grid-cols-3 gap-2">
                                            <select
                                                className="border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-slate-900"
                                                value={step.channel}
                                                onChange={e => updateCadenceStep(idx, 'channel', e.target.value)}
                                            >
                                                {['call','whatsapp','email','sms','meeting','linkedin','other'].map(c => (
                                                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                                                ))}
                                            </select>
                                            <div className="flex items-center gap-1">
                                                <span className="text-xs text-slate-500 whitespace-nowrap">Day +</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
                                                    value={step.days_offset}
                                                    onChange={e => updateCadenceStep(idx, 'days_offset', parseInt(e.target.value) || 0)}
                                                />
                                            </div>
                                            <input
                                                className="border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
                                                placeholder="Task description"
                                                value={step.description_template}
                                                onChange={e => updateCadenceStep(idx, 'description_template', e.target.value)}
                                            />
                                        </div>
                                        <button type="button" onClick={() => removeCadenceStep(idx)} className="text-red-400 hover:text-red-600 transition-colors">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" className="rounded-lg" onClick={() => setCadenceDialogOpen(false)}>Cancel</Button>
                            <Button className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white" onClick={saveCadence} disabled={savingCadence}>
                                {savingCadence ? 'Saving...' : (editingCadence ? 'Save Changes' : 'Create Cadence')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SettingsPage;
